import React, { useState } from "react"
import { ProfileConfirmation } from 'appDtos';
import { isEmptyString, isValidEmailAddress } from '@chr/online-shared-ui-validators';

import './profile-confirmation.scss'
import { CloseIcon } from '../../../common/components/svgs/CloseIcon';
import { ErrorIcon } from '../../../common/components/svgs/ErrorIcon';

export interface ProfileConfirmationFormProps {
  profile: ProfileConfirmation;
  onClose: () => void;
  onSubmit: (profile:ProfileConfirmation) => void;
}

const isGreaterThan = (str: string, max: number) => str ? str.length > max : false;

interface HelperTextProp {
  text: string;
  condition: boolean;
}

const HelperText = ({text, condition}:HelperTextProp) => (
  condition ? 
  <div>
    <ErrorIcon 
      data-testid="error-icon" 
      style={{
        marginRight:"5px", 
        verticalAlign: "middle"
      }} 
      fill="#da0303" 
      width="14px" 
      height="14px"
    /> 
    {text}
  </div> 
  : null
)

export const ProfileConfirmationForm = (props: ProfileConfirmationFormProps) => {
  const [ profile, setProfile ] = useState(props.profile);

  const hasError = ():boolean =>
    (isEmptyString(profile.firstName) ||
      isGreaterThan(profile.firstName, 50) ||
      isEmptyString(profile.lastName) ||
      isGreaterThan(profile.lastName, 50) ||
      isEmptyString(profile.companyName) ||
      isGreaterThan(profile.companyName, 100) ||
      isEmptyString(profile.email) ||
      !isValidEmailAddress(profile.email)
    ) ? true : false;

  return (
    <div className="container profile-confirmation">
      <CloseIcon onClick={props.onClose} id="close-btn" data-testid="close-btn" className="close" width="23px" height="23px" />
      <form className="margin-top-15" onSubmit={e => {e.preventDefault(); props.onSubmit(profile);}}>
        <h2>Confirm your Profile Information</h2>
        <div className="margin-top-20">
          In preparation for a change to a new, more secure sign-in process we need you to confirm your profile information
        </div>
        <div className="margin-top-20">
          {profile.profileInfoLabel ? profile.profileInfoLabel : "Username"}:
          <span id="userName" style={{paddingLeft: "5px"}}>
            <b>{profile.profileInfoValue}</b>
          </span>
        </div>

        <div className="row margin-top-20">
          <div className="col-xs-4 ">
            <div className={isEmptyString(profile.firstName) || isGreaterThan(profile.firstName, 50) ? "from-group has-error" : "form-group"}
              data-testid="from-group-firstname" >
              <label htmlFor="firstName">First Name</label>
              <input
                className="form-control"
                id="firstName"
                value={profile.firstName}
                onChange={e => setProfile({...profile, firstName:e.target.value})}
              />
              <HelperText condition={isEmptyString(profile.firstName)} text="First Name required"/> 
              <HelperText condition={isGreaterThan(profile.firstName, 50)} text="First Name cannot be more than 50 characters"/> 
            </div>
          </div>

          <div className="col-xs-4 ">
            <div className={isEmptyString(profile.lastName) || isGreaterThan(profile.lastName, 50) ? "from-group has-error" : "form-group"}
              data-testid="from-group-lastname" >
              <label htmlFor="lastName">Last Name</label>
              <input
                className="form-control"
                id="lastName"
                value={profile.lastName}
                onChange={e => setProfile({...profile, lastName:e.target.value})}
              />
              <HelperText condition={isEmptyString(profile.lastName)} text="Last Name required"/> 
              <HelperText condition={isGreaterThan(profile.lastName, 50)} text="Last Name cannot be more than 50 characters"/> 
            </div>
          </div>

          <div className="col-xs-4">
            <div className={isEmptyString(profile.companyName) || isGreaterThan(profile.companyName, 100) ? "from-group has-error" : "form-group" }
              data-testid="from-group-companyname" >
              <label htmlFor="companyName">Company Name</label>
              <input
                className="form-control"
                id="companyName"
                value={profile.companyName}
                onChange={e => setProfile({...profile, companyName:e.target.value})}
              />
              <HelperText condition={isEmptyString(profile.companyName)} text="Company Name required"/> 
              <HelperText condition={isGreaterThan(profile.companyName, 100)} text="Company Name cannot be more than 100 characters"/> 
            </div>
          </div>
        </div>

        <div className="row margin-top-5">
          <div className="col-xs-8">
            <div className={isEmptyString(profile.email) || !isValidEmailAddress(profile.email) ? "from-group has-error" : "form-group"}
              data-testid="from-group-email" >
              <label htmlFor="email">Email Address</label>
              <input
                className="form-control"
                id="email"
                value={profile.email}
                onChange={e => setProfile({...profile, email:e.target.value})}
              />
              <HelperText condition={isEmptyString(profile.email)} text="Email required"/> 
              <HelperText 
                condition={!isEmptyString(profile.email) && !isValidEmailAddress(profile.email)} 
                text="Invalid Email format"
              /> 
              <div className="input-info">Must be a valid email</div>
            </div>
          </div>
        </div>

        <div className="save-btn-container" >
          <div>
            Upon clicking "Save and Confirm Email" we will send an email to the address specified.
            You will need to click a link in this email to confirm you received it.
          </div>

          <div className="margin-top-20 float-right">
            <button disabled={hasError()} className="btn btn-primary" type="submit">Save and Confirm Email</button>
          </div>
        </div>
      </form>
    </div>
  );
}

